/**
 * Futures Control → Bank Statement (per bank).
 * Rebuild đúng Access form frmFuturesQryBankStatement{VTB,MBB,BIDV} + AddForm.
 * Grid lấy từ materialized view `public.mv_bank_statement` (đầy đủ cột derived).
 * Add dialog: hiển thị row gần nhất + auto-fill starting_balance = ending_balance trước đó.
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useEffect, useMemo, useState, type CSSProperties, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react'
import dayjs from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import {
  COMBOBOX_FILTER_COMPACT,
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../theme/responsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useDeleteBankStatementMutation,
  useGetBankStatementByDateQuery,
  useGetBankStatementLatestQuery,
  useGetFuturesBankStatementQuery,
  useUpsertBankStatementMutation,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import { useFuturesColumns } from './useFuturesColumns'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
} from './futuresAccessUi'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

// Bank picker = navigation/filter selector — dùng COMBOBOX_FILTER_COMPACT
// (transform scale xs để compact mà KHÔNG đụng input fontSize → no iOS zoom).
// readOnly trên renderInput TextField để mobile không bung keyboard (user chỉ
// chọn từ 3 options, không gõ).

const BANK_OPTIONS = ['Vietinbank', 'MBbank', 'BIDV'] as const

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/** Cột freeze khi scroll ngang — Notice Date (identifier theo ngày). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['date'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  date: 120,
}

/** Đúng thứ tự + nhãn ngữ nghĩa từ frmFuturesQryBankStatement{VTB,MBB,BIDV}. */
const COLUMNS_STATEMENT: ColumnDef[] = [
  { key: 'date', label: 'Notice Date', kind: 'date' },
  { key: 'bank', label: 'Bank', filter: 'checkboxes' },
  { key: 'startingBalanceCal', label: 'Opening Balance', kind: 'number', align: 'right' },
  { key: 'marginInOrOut', label: 'Margin Deposit/Withdraw', kind: 'number', align: 'right' },
  { key: 'feeTransaction', label: 'Transaction Fee', kind: 'number', align: 'right' },
  { key: 'feeOther', label: 'Other Fee (*)', kind: 'number', align: 'right' },
  { key: 'interestReceiveOrPay', label: 'Interest Receivable/Payable', kind: 'number', align: 'right' },
  { key: 'profitOrLossReal', label: 'Realized P&L', kind: 'number', align: 'right' },
  { key: 'endingBalanceCal', label: 'Closing Balance', kind: 'number', align: 'right' },
  { key: 'profitOrLossPredict', label: 'Expected P&L', kind: 'number', align: 'right' },
  { key: 'sumEndingbalanceProfitorlosspredict', label: 'Closing Balance + Expected P&L', kind: 'number', align: 'right' },
  { key: 'marginMaintanceIm', label: 'Maintenance Margin (MM)', kind: 'number', align: 'right' },
  { key: 'marginExcess', label: 'Margin Excess', kind: 'number', align: 'right' },
  { key: 'marginInitial', label: 'Initial Margin (IM)', kind: 'number', align: 'right' },
  { key: 'marginExcessUsd', label: 'Margin Excess (USD)', kind: 'number', align: 'right' },
  { key: 'marginInitialUsd', label: 'Initial Margin (IM) (USD)', kind: 'number', align: 'right' },
  { key: 'endingBalanceCalUsd', label: 'Closing Balance (USD)', kind: 'number', align: 'right' },
  { key: 'marginSurplusOrDeficit', label: 'Margin Surplus/Deficit', kind: 'number', align: 'right' },
  { key: 'rateBuyCurrency', label: 'FX Buy Rate', kind: 'number', align: 'right' },
  { key: 'rateSellCurrency', label: 'FX Sell Rate', kind: 'number', align: 'right' },
  { key: 'cashInUsd', label: 'Cash in USD', kind: 'number', align: 'right' },
  { key: 'profitOrLossRealUsd', label: 'Realized P&L (USD)', kind: 'number', align: 'right' },
  { key: 'profitOrLossPredictUsd', label: 'Expected P&L (USD)', kind: 'number', align: 'right' },
  { key: 'feeTransactionUsd', label: 'Transaction Fee (USD)', kind: 'number', align: 'right' },
  { key: 'feeOtherUsd', label: 'Other Fee (USD)', kind: 'number', align: 'right' },
  { key: 'interestReceiveOrPayUsd', label: 'Interest Rcv/Pay (USD)', kind: 'number', align: 'right' },
  { key: 'feeTransaction2', label: 'Transaction Fee (2)', kind: 'number', align: 'right' },
  { key: 'feeTransactionConst', label: 'Fixed Transaction Fee', kind: 'number', align: 'right' },
  { key: 'note', label: 'Note' },
  { key: 'dateUpdated', label: 'Updated At', kind: 'datetime' },
  { key: 'personUpdated', label: 'Updated By' },
]

/** Snapshot/rates/derived USD đều không nên cộng dồn theo trang. */
const NON_SUM_STMT = new Set<string>([
  'startingBalanceCal',
  'endingBalanceCal',
  'sumEndingbalanceProfitorlosspredict',
  'marginMaintanceIm',
  'marginExcess',
  'marginInitial',
  'marginExcessUsd',
  'marginInitialUsd',
  'endingBalanceCalUsd',
  'marginSurplusOrDeficit',
  'rateBuyCurrency',
  'rateSellCurrency',
  'feeTransactionConst',
])

const STORAGE_COLS_BANK_STMT = 'futures-cols-bank-statement-v2'

type BankName = 'Vietinbank' | 'MBbank' | 'BIDV'
type BankGridRow = Record<string, unknown> & { id: string }

function str(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

function optNum(s: string): number | undefined {
  const t = s.replace(/,/g, '').trim()
  if (t === '' || t === '-' || t === '.') return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : undefined
}

/** Validation theo Access btnSave_Click (frmFuturesQryBankStatement{BIDV,VTB,MBB}Add).
 *  errCheck 2..13 áp dụng cho mọi bank; 14 chỉ áp cho MBbank (fee_transaction_const).
 *  Trả về thông điệp đầu tiên gặp, null nếu OK. Bỏ qua errCheck=1 (Bank-Date is exists)
 *  vì BE đã xử lý qua upsert ON CONFLICT — lỗi sẽ hiện qua snack. */
function validateBankStmtForm(dlg: DialogState, bank: BankName): string | null {
  if (!dlg.date) return 'Notice Date is required.'
  if (optNum(dlg.marginInOrOut) === undefined) return 'Please enter Margin Deposit/Withdraw.'
  if (optNum(dlg.feeTransaction) === undefined) return 'Please enter Transaction Fee.'
  if (optNum(dlg.feeOther) === undefined) return 'Please enter Other Fee.'
  if (optNum(dlg.interestReceiveOrPay) === undefined) return 'Please enter Interest Receivable/Payable.'
  if (optNum(dlg.profitOrLossReal) === undefined) return 'Please enter Realized P&L.'
  if (optNum(dlg.profitOrLossPredict) === undefined) return 'Please enter Expected P&L.'
  if (optNum(dlg.marginMaintanceIm) === undefined) return 'Please enter Maintenance Margin (MM).'
  if (optNum(dlg.marginExcess) === undefined) return 'Please enter Margin Excess.'
  if (optNum(dlg.marginInitial) === undefined) return 'Please enter Initial Margin (IM).'
  if (optNum(dlg.rateBuyCurrency) === undefined) return 'Please enter FX Buy Rate.'
  if (optNum(dlg.rateSellCurrency) === undefined) return 'Please enter FX Sell Rate.'
  if (bank === 'MBbank' && optNum(dlg.feeTransactionConst) === undefined) {
    return 'Please enter Fixed Transaction Fee.'
  }
  return null
}

/** Giữ digits + tối đa 1 dấu chấm + dấu trừ ở đầu. Loại bỏ mọi ký tự khác. */
function sanitizeDecimal(raw: string): string {
  if (!raw) return ''
  const negative = raw.trim().startsWith('-')
  const cleaned = raw.replace(/[^0-9.]/g, '')
  const dot = cleaned.indexOf('.')
  const oneDot =
    dot === -1 ? cleaned : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '')
  return negative ? `-${oneDot}` : oneDot
}

/** Chặn ký tự không phải số ngay tại keydown (giữ Backspace/Delete/Tab/mũi tên/Enter). */
function blockNonNumericKey(e: KeyboardEvent<HTMLInputElement>) {
  if (e.ctrlKey || e.metaKey || e.altKey) return
  const allowed = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
  ]
  if (allowed.includes(e.key)) return
  if (/^[0-9]$/.test(e.key)) return
  if (e.key === '.' || e.key === '-') return
  e.preventDefault()
}

function formatThousands(raw: string): string {
  const t = raw.replace(/,/g, '').trim()
  if (t === '' || t === '-' || t === '.') return t
  const n = Number(t)
  if (!Number.isFinite(n)) return raw
  if (Number.isInteger(n)) return n.toLocaleString('en-US')
  const [intPart, decPart = ''] = t.split('.')
  const intNum = Number(intPart || '0')
  return `${intNum.toLocaleString('en-US')}.${decPart}`
}

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {children}
    </Box>
  )
}

type NumberFieldProps = Omit<ComponentProps<typeof TextField>, 'onChange' | 'value'> & {
  value: string
  onChange: (next: string) => void
}

function NumberField({ value, onChange, inputProps, sx, ...rest }: NumberFieldProps) {
  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(sanitizeDecimal(e.target.value))}
      onBlur={(e) => {
        const formatted = formatThousands(e.target.value)
        if (formatted !== e.target.value) onChange(formatted)
      }}
      onFocus={(e) => {
        const raw = e.target.value.replace(/,/g, '')
        if (raw !== e.target.value) onChange(raw)
      }}
      inputProps={{
        inputMode: 'decimal',
        onKeyDown: blockNonNumericKey,
        style: { textAlign: 'right' },
        ...inputProps,
      }}
      sx={{ '& input': { fontVariantNumeric: 'tabular-nums' }, ...(sx as object) }}
      {...rest}
    />
  )
}

function cellValue(row: Record<string, unknown>, col: ColumnDef): string {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  return String(v)
}

function sumNumericOnPage(pageRows: BankGridRow[], col: ColumnDef): number | null {
  if (col.kind !== 'number' || NON_SUM_STMT.has(col.key)) return null
  let total = 0
  let count = 0
  for (const row of pageRows) {
    const v = row[col.key]
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v
      count += 1
    }
  }
  return count > 0 ? total : null
}

interface Props {
  embedded?: boolean
}

interface DialogState {
  open: boolean
  creating: boolean
  date: string
  startingBalance: string
  endingBalance: string
  marginInOrOut: string
  feeTransaction: string
  feeOther: string
  interestReceiveOrPay: string
  profitOrLossReal: string
  profitOrLossPredict: string
  marginMaintanceIm: string
  marginExcess: string
  marginInitial: string
  marginExcessUsd: string
  marginInitialUsd: string
  rateBuyCurrency: string
  rateSellCurrency: string
  fx: string
  feeTransactionConst: string
  note: string
}

const EMPTY_DIALOG: DialogState = {
  open: false,
  creating: true,
  date: '',
  startingBalance: '',
  endingBalance: '',
  marginInOrOut: '',
  feeTransaction: '',
  feeOther: '',
  interestReceiveOrPay: '',
  profitOrLossReal: '',
  profitOrLossPredict: '',
  marginMaintanceIm: '',
  marginExcess: '',
  marginInitial: '',
  marginExcessUsd: '',
  marginInitialUsd: '',
  rateBuyCurrency: '',
  rateSellCurrency: '',
  fx: '',
  feeTransactionConst: '',
  note: '',
}

export default function FuturesBankStatementView({ embedded: _embedded }: Props) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [bank, setBank] = useState<BankName>('Vietinbank')

  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [pagination, setPagination] = useState({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()

  const queryArgs = {
    bank,
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  }
  const { data, isLoading, isFetching, error, refetch } = useGetFuturesBankStatementQuery(queryArgs)

  const [upsertStmt, us] = useUpsertBankStatementMutation()
  const [delStmt, ds] = useDeleteBankStatementMutation()

  const columnsCtrl = useFuturesColumns({
    mode: 'bank-statement',
    allColumns: COLUMNS_STATEMENT,
    storageKey: STORAGE_COLS_BANK_STMT,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('futures.bank-statement')

  const baseRows = useMemo((): BankGridRow[] => {
    const items = data?.items ?? []
    return items.map((r, idx) => {
      const rec = r as Record<string, unknown>
      const dk = str(rec.date ?? '').slice(0, 10)
      return { ...rec, id: `stmt-${idx}-${dk}` }
    })
  }, [data?.items])

  /* ── Filter pipeline: search ∧ per-column ▽ ─────────────────────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, COLUMNS_STATEMENT) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  /* ── Pagination clamp ───────────────────────────────────────────── */
  const rows = useMemo((): BankGridRow[] => {
    if (pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  const firstRow = useMemo((): BankGridRow | null => {
    if (!selectedRowId) return null
    return baseRows.find((r) => r.id === selectedRowId) ?? null
  }, [baseRows, selectedRowId])

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () =>
      colPopover
        ? COLUMNS_STATEMENT.find((c) => c.key === colPopover.key) ?? null
        : null,
    [colPopover],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  const totalRowValues = useMemo(
    () => new Map(visibleColumns.map((col) => [col.key, sumNumericOnPage(rows, col)] as const)),
    [rows, visibleColumns],
  )

  const gridKey = `${pagination.page}:${pagination.pageSize}:${rows.length}:${bank}`
  const minTableWidth = visibleColumns.length * 110

  const [dlg, setDlg] = useState<DialogState>(EMPTY_DIALOG)
  const [deletePk, setDeletePk] = useState<{ date: string; bank: BankName } | null>(null)

  // Access `date_AfterUpdate`: previous-row query `date < Me.date`.
  const latestQ = useGetBankStatementLatestQuery(
    { bank, before: dlg.date || undefined },
    { skip: !dlg.open },
  )
  const latestRow = (latestQ.data?.item ?? null) as Record<string, unknown> | null

  // Access `Form_Load`: absolute-latest query (no date filter) — snapshot `ending_balance_old`.
  // Dùng làm fallback khi user nhập date sớm hơn mọi row hiện có (recordCount = 0 ở date_AfterUpdate).
  const absLatestQ = useGetBankStatementLatestQuery(
    { bank },
    { skip: !dlg.open },
  )
  const endingBalanceOld = useMemo<number | null>(() => {
    const row = absLatestQ.data?.item as Record<string, unknown> | null | undefined
    if (!row) return null
    const v = row['endingBalanceCal']
    return typeof v === 'number' && Number.isFinite(v) ? v : null
  }, [absLatestQ.data])

  // Access `date_AfterUpdate` query #2: same-day row (date = Me.date) cho duplicate detection.
  // Khi có row → date.ForeColor = RED + auto-load 12 field; khi không → BLACK + clear 12 field.
  // 1:1 Access: chỉ chạy khi creating + có date + có previous-row (latestRow) — nested theo VBA.
  const sameDayQ = useGetBankStatementByDateQuery(
    { bank, date: dlg.date },
    { skip: !dlg.open || !dlg.creating || !dlg.date || !latestRow },
  )
  const sameDayRow = (sameDayQ.data?.item ?? null) as Record<string, unknown> | null
  const sameDayDuplicate = !!sameDayRow

  // Access `date_AfterUpdate` (nested branch): auto-load 12 field từ same-day row.
  // Khi same-day exists → fill các field; khi same-day null nhưng vẫn ở nested branch
  // (creating + date + latestRow) → clear các field. Khớp với Access "If recordCount > 0 / Else clear".
  useEffect(() => {
    if (!dlg.open || !dlg.creating || !dlg.date || !latestRow) return
    const numStr = (v: unknown): string => {
      if (v == null || v === '') return ''
      const n = typeof v === 'number' ? v : Number(String(v))
      return Number.isFinite(n) ? formatThousands(String(n)) : ''
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync RTK Query result → form state (Access date_AfterUpdate)
    setDlg((s) => ({
      ...s,
      marginInOrOut: numStr(sameDayRow?.['marginInOrOut']),
      interestReceiveOrPay: numStr(sameDayRow?.['interestReceiveOrPay']),
      feeTransaction: numStr(sameDayRow?.['feeTransaction']),
      feeOther: numStr(sameDayRow?.['feeOther']),
      profitOrLossReal: numStr(sameDayRow?.['profitOrLossReal']),
      profitOrLossPredict: numStr(sameDayRow?.['profitOrLossPredict']),
      marginExcess: numStr(sameDayRow?.['marginExcess']),
      marginInitial: numStr(sameDayRow?.['marginInitial']),
      marginMaintanceIm: numStr(sameDayRow?.['marginMaintanceIm']),
      rateBuyCurrency: numStr(sameDayRow?.['rateBuyCurrency']),
      rateSellCurrency: numStr(sameDayRow?.['rateSellCurrency']),
      note: str(sameDayRow?.['note'] ?? ''),
    }))
  }, [dlg.open, dlg.creating, dlg.date, latestRow, sameDayRow])

  /** Access `date_AfterUpdate` net-logic cho starting_balance:
   *    starting = prev_row.ending_balance_cal  CHỈ KHI (có prev: date < entered) VÀ
   *               (có row cùng ngày: date = entered);
   *    else (no prev | prev nhưng không same-day | no date) → ending_balance_old
   *               (snapshot Form_Load = absolute latest, ORDER BY date DESC).
   *  VBA: nhánh `Else` của cả `if prev` lẫn `if same-day` đều gán
   *  `Me.starting_balance = Me.ending_balance_old`. Trường hợp thêm xuôi thời
   *  gian (prev == latest) hai giá trị trùng nhau; chỉ khác khi back-dated. */
  const computedStartingBalance = useMemo(() => {
    if (!dlg.creating) return dlg.startingBalance
    const prev = latestRow?.['endingBalanceCal']
    if (sameDayDuplicate && typeof prev === 'number' && Number.isFinite(prev)) {
      return formatThousands(String(prev))
    }
    if (endingBalanceOld != null) return formatThousands(String(endingBalanceOld))
    return ''
  }, [dlg.creating, dlg.startingBalance, latestRow, endingBalanceOld, sameDayDuplicate])

  /** Access auto-compute ending_balance (After_Update của date / margin_in_or_out / fee_transaction /
   *  fee_other / interest_receive_or_pay / profit_or_loss_real):
   *  ending_balance = starting_balance + margin_in_or_out + interest + fee_tx + fee_other + p/l real. */
  const computedEndingBalance = useMemo(() => {
    const start = optNum(computedStartingBalance) ?? 0
    const mio = optNum(dlg.marginInOrOut) ?? 0
    const intr = optNum(dlg.interestReceiveOrPay) ?? 0
    const ftx = optNum(dlg.feeTransaction) ?? 0
    const foth = optNum(dlg.feeOther) ?? 0
    const plr = optNum(dlg.profitOrLossReal) ?? 0
    return formatThousands(String(start + mio + intr + ftx + foth + plr))
  }, [
    computedStartingBalance,
    dlg.marginInOrOut,
    dlg.interestReceiveOrPay,
    dlg.feeTransaction,
    dlg.feeOther,
    dlg.profitOrLossReal,
  ])

  const openNew = () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = `${today.getMonth() + 1}`.padStart(2, '0')
    const dd = `${today.getDate()}`.padStart(2, '0')
    setDlg({ ...EMPTY_DIALOG, open: true, creating: true, date: `${yyyy}-${mm}-${dd}` })
  }

  const openEdit = () => {
    const r = firstRow
    if (!r) return
    const numStr = (v: unknown): string => {
      if (v == null || v === '') return ''
      const n = typeof v === 'number' ? v : Number(String(v))
      return Number.isFinite(n) ? formatThousands(String(n)) : ''
    }
    setDlg({
      open: true,
      creating: false,
      date: str(r['date']).slice(0, 10),
      // bank_statement không lưu starting_balance — MV cung cấp `starting_balance_cal`
      startingBalance: numStr(r['startingBalanceCal']),
      endingBalance: numStr(r['endingBalance'] ?? r['endingBalanceCal']),
      marginInOrOut: numStr(r['marginInOrOut']),
      feeTransaction: numStr(r['feeTransaction']),
      feeOther: numStr(r['feeOther']),
      interestReceiveOrPay: numStr(r['interestReceiveOrPay']),
      profitOrLossReal: numStr(r['profitOrLossReal']),
      profitOrLossPredict: numStr(r['profitOrLossPredict']),
      marginMaintanceIm: numStr(r['marginMaintanceIm']),
      marginExcess: numStr(r['marginExcess']),
      marginInitial: numStr(r['marginInitial']),
      marginExcessUsd: numStr(r['marginExcessUsd']),
      marginInitialUsd: numStr(r['marginInitialUsd']),
      rateBuyCurrency: numStr(r['rateBuyCurrency']),
      rateSellCurrency: numStr(r['rateSellCurrency']),
      fx: numStr(r['fx']),
      feeTransactionConst: numStr(r['feeTransactionConst']),
      note: str(r['note'] ?? ''),
    })
  }

  const save = async () => {
    const err = validateBankStmtForm(dlg, bank)
    if (err) {
      snack.error(err)
      return
    }
    try {
      await upsertStmt({
        date: dlg.date,
        bank,
        // ending_balance auto-compute từ formula Access (sau khi validate đã pass).
        endingBalance: optNum(computedEndingBalance),
        marginInOrOut: optNum(dlg.marginInOrOut),
        feeTransaction: optNum(dlg.feeTransaction),
        feeOther: optNum(dlg.feeOther),
        interestReceiveOrPay: optNum(dlg.interestReceiveOrPay),
        profitOrLossReal: optNum(dlg.profitOrLossReal),
        profitOrLossPredict: optNum(dlg.profitOrLossPredict),
        marginMaintanceIm: optNum(dlg.marginMaintanceIm),
        marginExcess: optNum(dlg.marginExcess),
        marginInitial: optNum(dlg.marginInitial),
        marginExcessUsd: optNum(dlg.marginExcessUsd),
        marginInitialUsd: optNum(dlg.marginInitialUsd),
        rateBuyCurrency: optNum(dlg.rateBuyCurrency),
        rateSellCurrency: optNum(dlg.rateSellCurrency),
        fx: optNum(dlg.fx),
        feeTransactionConst: optNum(dlg.feeTransactionConst),
        note: dlg.note || undefined,
      }).unwrap()
      snack.success('Saved.')
      setDlg(EMPTY_DIALOG)
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const confirmDelete = async () => {
    if (!deletePk) return
    try {
      await delStmt({ date: deletePk.date, bank: deletePk.bank }).unwrap()
      snack.success('Row soft-deleted.')
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const removeClick = () => {
    const r = firstRow
    if (!r?.['date']) return
    const d = str(r['date']).slice(0, 10)
    setDeletePk({ date: d, bank })
  }

  const busy = us.isLoading || ds.isLoading

  return (
    <>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Autocomplete
                size="small"
                autoHighlight
                disableClearable
                options={BANK_OPTIONS as readonly BankName[]}
                value={bank}
                onChange={(_, v) => {
                  setBank(v)
                  setPagination((p) => ({ ...p, page: 0 }))
                  setSelectedRowId(null)
                }}
                isOptionEqualToValue={(a, b) => a === b}
                sx={COMBOBOX_FILTER_COMPACT}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Bank"
                    // readOnly: mobile không bung keyboard (Pattern 14 nav picker)
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
              {canEdit ? (
                <>
                  <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} onClick={openNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
                    <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
                  </Button>
                  <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!firstRow} onClick={openEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit">
                    <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
                  </Button>
                  <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!firstRow} onClick={removeClick} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete">
                    <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
                  </Button>
                </>
              ) : null}
            </Stack>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
              placeholder="Date, note, balances, fees…"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {baseRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE ? 0 : pagination.page}
                onPageChange={(_, newPage) => {
                  setPagination((p) => ({ ...p, page: newPage }))
                  setSelectedRowId(null)
                }}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={(e) => {
                  setPagination({ page: 0, pageSize: Number(e.target.value) })
                  setSelectedRowId(null)
                }}
                rowsPerPageOptions={[25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load bank statement"
                description="Try again or contact support if the error persists."
                action={
                  <Button size="small" variant="outlined" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading && rows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : rows.length === 0 ? (
              <BrandState title="No data" description="Pick a bank, adjust search, or add a row." />
            ) : (
              <TableVirtuoso
                key={gridKey}
                style={{ height: '100%' }}
                data={rows}
                fixedHeaderContent={() => (
                  <TableRow>
                    {visibleColumns.map((col) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      const headerStyle: CSSProperties = {}
                      if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                      if (isFrozen) {
                        headerStyle.position = 'sticky'
                        headerStyle.top = 0
                        headerStyle.left = 0
                        headerStyle.zIndex = 100
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className="app-table-header-cell"
                          style={Object.keys(headerStyle).length ? headerStyle : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            userSelect: 'none',
                            ...(isFrozen ? {} : { position: 'relative' }),
                          }}
                        >
                          {col.label}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(col.key)}
                            onClick={(position) =>
                              setColPopover({ key: col.key, position })
                            }
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    {visibleColumns.map((col, index) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const total = totalRowValues.get(col.key)
                      const val = total == null ? '' : formatAccessNumber(total)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {index === 0 ? 'Total' : val}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_: number, row: BankGridRow) =>
                  visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const val = cellValue(row, col)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })
                }
                components={{
                  Table: (props: any) => (
                    <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                  ),
                  TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                  TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                  TableRow: (props: any) => {
                    const idx = Number((props as Record<string, unknown>)['data-item-index'])
                    const r = Number.isFinite(idx) ? rows[idx] : null
                    const rid = r?.id ?? null
                    return (
                      <TableRow
                        {...props}
                        hover
                        selected={rid != null && rid === selectedRowId}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (!rid) return
                          toggleSelectRow(rid)
                        }}
                      />
                    )
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={deletePk != null}
        title="Delete bank statement row"
        message={deletePk ? `Soft-delete ${deletePk.bank} — ${deletePk.date}?` : ''}
        danger
        loading={ds.isLoading}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onCancel={() => setDeletePk(null)}
        onClose={() => setDeletePk(null)}
        onConfirm={() => void confirmDelete()}
      />

      <Dialog
        open={dlg.open}
        onClose={() => setDlg(EMPTY_DIALOG)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={futuresSaleContractDialogPaperProps}
      >
        <DialogTitle sx={futuresFormDialogTitleSx}>
          <Typography component="span" sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>
            {dlg.creating
              ? `Futures Control - Bank Statement (${bank})`
              : `Edit bank statement — ${bank}`}
          </Typography>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => void save()}
              disabled={busy || !dlg.date}
              sx={futuresFormDialogActionBtnSx}
            >
              {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setDlg(EMPTY_DIALOG)} sx={futuresFormDialogActionBtnSx}>
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {/* Header row: Date + Bank chip */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Notice Date *"
                  format="DD-MMM-YYYY"
                  value={dlg.date ? dayjs(dlg.date) : null}
                  onChange={(d) => setDlg((s) => ({ ...s, date: d && d.isValid() ? d.format('YYYY-MM-DD') : '' }))}
                  disabled={!dlg.creating}
                  slotProps={{ textField: {
                    size: 'small',
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    error: sameDayDuplicate,
                    helperText: sameDayDuplicate ? 'A statement already exists for this date — Save will overwrite it.' : undefined,
                  } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Bank"
                  value={bank}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Change in the filter bar"
                />
              </Grid>
            </Grid>

            {dlg.creating ? (
              <Box
                sx={{
                  bgcolor: (t) => alpha(t.palette.info.main, 0.08),
                  border: (t) => `1px dashed ${alpha(t.palette.info.main, 0.4)}`,
                  borderRadius: 1,
                  p: 1.25,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Most recent statement before this date
                </Typography>
                {latestQ.isFetching ? (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption">Loading…</Typography>
                  </Stack>
                ) : latestRow ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 0.5 }}>
                    <Typography variant="caption">
                      Latest statement date: <b>{formatAccessDate(latestRow['date'])}</b>
                    </Typography>
                    <Typography variant="caption">
                      Latest opening balance:{' '}
                      <b>
                        {typeof latestRow['startingBalanceCal'] === 'number'
                          ? formatAccessNumber(latestRow['startingBalanceCal'] as number)
                          : '—'}
                      </b>
                    </Typography>
                    <Typography variant="caption">
                      Latest closing balance:{' '}
                      <b>
                        {typeof latestRow['endingBalanceCal'] === 'number'
                          ? formatAccessNumber(latestRow['endingBalanceCal'] as number)
                          : (typeof latestRow['endingBalance'] === 'number'
                              ? formatAccessNumber(latestRow['endingBalance'] as number)
                              : '—')}
                      </b>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    No prior statement for {bank}.
                  </Typography>
                )}
              </Box>
            ) : null}

            {/* Thứ tự sections theo frmFuturesQryBankStatement{BIDV,MBB,VTB}Add.
                Access flow: phát sinh trong phiên → ký quỹ → tỷ giá → số dư (computed) → note. */}
            <FormSection title="Session transactions">
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Margin Deposit/Withdraw"
                    value={dlg.marginInOrOut}
                    onChange={(v) => setDlg((s) => ({ ...s, marginInOrOut: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Transaction Fee"
                    value={dlg.feeTransaction}
                    onChange={(v) => setDlg((s) => ({ ...s, feeTransaction: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Other Fee (*)"
                    value={dlg.feeOther}
                    onChange={(v) => setDlg((s) => ({ ...s, feeOther: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Interest Receivable/Payable"
                    value={dlg.interestReceiveOrPay}
                    onChange={(v) => setDlg((s) => ({ ...s, interestReceiveOrPay: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Realized P&L"
                    value={dlg.profitOrLossReal}
                    onChange={(v) => setDlg((s) => ({ ...s, profitOrLossReal: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Expected P&L"
                    value={dlg.profitOrLossPredict}
                    onChange={(v) => setDlg((s) => ({ ...s, profitOrLossPredict: v }))}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Margin">
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Maintenance Margin (MM)"
                    value={dlg.marginMaintanceIm}
                    onChange={(v) => setDlg((s) => ({ ...s, marginMaintanceIm: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Margin Excess"
                    value={dlg.marginExcess}
                    onChange={(v) => setDlg((s) => ({ ...s, marginExcess: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="Initial Margin (IM)"
                    value={dlg.marginInitial}
                    onChange={(v) => setDlg((s) => ({ ...s, marginInitial: v }))}
                  />
                </Grid>
                {bank === 'MBbank' ? (
                  <Grid item xs={12} sm={6}>
                    <NumberField
                      label="Fixed Transaction Fee"
                      value={dlg.feeTransactionConst}
                      onChange={(v) => setDlg((s) => ({ ...s, feeTransactionConst: v }))}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </FormSection>

            <FormSection title="FX rates">
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="FX Buy Rate"
                    value={dlg.rateBuyCurrency}
                    onChange={(v) => setDlg((s) => ({ ...s, rateBuyCurrency: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberField
                    label="FX Sell Rate"
                    value={dlg.rateSellCurrency}
                    onChange={(v) => setDlg((s) => ({ ...s, rateSellCurrency: v }))}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Balance">
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Opening Balance (auto)"
                    value={computedStartingBalance}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
                    helperText="= Closing balance of the previous statement (not stored)."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Closing Balance (auto)"
                    value={computedEndingBalance}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
                    helperText="Auto: = Opening Balance + Margin Deposit/Withdraw + Interest Rcv/Pay + Transaction Fee + Other Fee + Realized P&L."
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* fx, margin_excess_usd, margin_initial_usd KHÔNG có trong frmFuturesQryBankStatement{BIDV,MBB,VTB}Add.
                Giữ ở phần "Bổ sung" để vẫn ghi vào DB (cột thật) — tuỳ chọn, có thể bỏ trống. */}
            <FormSection title="Additional fields">
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="FX"
                    value={dlg.fx}
                    onChange={(v) => setDlg((s) => ({ ...s, fx: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="Margin Excess (USD)"
                    value={dlg.marginExcessUsd}
                    onChange={(v) => setDlg((s) => ({ ...s, marginExcessUsd: v }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="Initial Margin (IM) (USD)"
                    value={dlg.marginInitialUsd}
                    onChange={(v) => setDlg((s) => ({ ...s, marginInitialUsd: v }))}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Note">
              <TextField
                label="Note"
                value={dlg.note}
                onChange={(e) => setDlg((s) => ({ ...s, note: e.target.value }))}
                size="small"
                fullWidth
                multiline
                minRows={2}
              />
            </FormSection>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
